import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/design-system.css';

function Terms() {
  return (
    <div className="ps-page">
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <Link to="/" className="ps-btn ps-btn--ghost ps-btn--sm">← Home</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container" style={{ maxWidth:'820px', paddingTop:'40px', paddingBottom:'80px' }}>
        <div style={{ marginBottom:'36px' }}>
          <span style={{ display:'inline-block', fontSize:'12px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#4f46e5', marginBottom:'12px' }}>Legal</span>
          <h1 style={{ fontFamily:'Inter, sans-serif', fontSize:'clamp(28px,4vw,40px)', fontWeight:800, letterSpacing:'-0.6px', marginBottom:'10px' }}>Terms of Service</h1>
          <p style={{ fontSize:'14px', color:'#8b8aab' }}>Last updated: March 27, 2026</p>
        </div>

        <div className="ps-card" style={{ lineHeight:'1.8', fontSize:'15px', color:'#4b4a6b' }}>
          <LegalSection title="1. Acceptance of Terms">
            <p>By accessing or using Postrify, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using the service.</p>
          </LegalSection>

          <LegalSection title="2. Description of Service">
            <p>Postrify is an AI-powered social media content management platform that provides:</p>
            <ul>
              <li>AI-generated content creation for social media posts</li>
              <li>AI-generated image creation with brand logo overlays</li>
              <li>Scheduled and automated publishing to Facebook and Instagram</li>
              <li>Content scheduling and multi-brand management tools</li>
              <li>OAuth-based social account integration</li>
            </ul>
            <p style={{marginTop:'12px'}}><strong style={{color:'#16152e'}}>AI-Generated Content Disclaimer:</strong> All content generated by Postrify is created using AI. While we strive for quality, AI content may contain inaccuracies, biases, or require human review. <em>You are fully responsible for reviewing and approving all content before publication.</em></p>
          </LegalSection>

          <LegalSection title="3. User Eligibility &amp; Responsibilities">
            <p>By using Postrify, you confirm that:</p>
            <ul>
              <li>You are at least 13 years of age (18 if required by your jurisdiction)</li>
              <li>You have the authority to bind your organization to these Terms</li>
              <li>You will provide accurate and complete information</li>
              <li>You will maintain the security of your account credentials</li>
              <li>You own or have authorization to manage the social media accounts you connect</li>
              <li>You use the service in compliance with all applicable laws and regulations</li>
              <li>You have the right to use, publish, and authorize our use of all content uploaded</li>
            </ul>
          </LegalSection>

          <LegalSection title="4. Content Ownership &amp; Responsibility">
            <p><strong style={{color:'#16152e'}}>Your Content:</strong> You retain full ownership of your brand information, logos, and any content you provide to Postrify.</p>
            <p><strong style={{color:'#16152e'}}>Generated Content:</strong> AI-generated content is provided for your use as-is. You are <em>solely responsible</em> for:</p>
            <ul>
              <li>Reviewing all generated content before publication</li>
              <li>Ensuring content complies with platform policies (Facebook Community Standards, Instagram Community Guidelines)</li>
              <li>Ensuring content does not violate any laws or third-party rights</li>
              <li>Obtaining necessary approvals or consent (e.g., for posts mentioning other brands or individuals)</li>
              <li>All consequences of content you publish</li>
            </ul>
            <p style={{marginTop:'12px'}}><strong style={{color:'#16152e'}}>License to Postrify:</strong> You grant Postrify a limited license to use your content solely to provide the service. We will not use your content for any other purpose.</p>
          </LegalSection>

          <LegalSection title="5. Social Media Platform Integration">
            <p><strong style={{color:'#16152e'}}>OAuth Authorization:</strong> When you connect your Facebook or Instagram account, you authorize Postrify to publish content to your selected pages/accounts on your behalf.</p>
            <p><strong style={{color:'#16152e'}}>Compliance with Platform Terms:</strong> Your use of Postrify to post on Facebook and Instagram must comply with:</p>
            <ul>
              <li><a href="https://www.facebook.com/policies_center/" target="_blank" rel="noopener noreferrer" style={{color:'#4f46e5', fontWeight:600}}>Facebook Community Standards</a> and <a href="https://www.facebook.com/legal/terms/update" target="_blank" rel="noopener noreferrer" style={{color:'#4f46e5', fontWeight:600}}>Terms of Service</a></li>
              <li><a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer" style={{color:'#4f46e5', fontWeight:600}}>Instagram Community Guidelines</a> and <a href="https://www.instagram.com/legal/terms/" target="_blank" rel="noopener noreferrer" style={{color:'#4f46e5', fontWeight:600}}>Terms of Service</a></li>
              <li><a href="https://developers.facebook.com/policy" target="_blank" rel="noopener noreferrer" style={{color:'#4f46e5', fontWeight:600}}>Facebook Platform Policies</a></li>
            </ul>
            <p style={{marginTop:'12px'}}><strong style={{color:'#16152e'}}>Platform Violations:</strong> If content published via Postrify violates platform policies, you acknowledge that Facebook/Instagram may remove content, suspend your account, or take other enforcement actions. Postrify is not responsible for platform enforcement actions.</p>
          </LegalSection>

          <LegalSection title="6. Third-Party Services &amp; APIs">
            <p>Postrify integrates with the following third-party services:</p>
            <ul>
              <li><strong>Google Gemini API</strong> — AI text generation</li>
              <li><strong>Ghost/Infip API</strong> — AI image generation</li>
              <li><strong>Facebook Graph API</strong> — Page management and content publishing</li>
              <li><strong>Instagram Graph API</strong> — Account access and content publishing</li>
              <li><strong>Cloudinary</strong> — Image hosting and CDN delivery</li>
            </ul>
            <p>Use of these services is subject to their respective Terms of Service and Privacy Policies. Postrify is not responsible for third-party service failures, downtime, or policy changes.</p>
          </LegalSection>

          <LegalSection title="7. Prohibited Activities">
            <p>You may not:</p>
            <ul>
              <li>Use the service for illegal, fraudulent, or deceptive purposes</li>
              <li>Generate, publish, or distribute harmful, abusive, hateful, defamatory, or offensive content</li>
              <li>Violate intellectual property rights or impersonate others</li>
              <li>Use bots or automated tools to circumvent usage limits or security measures</li>
              <li>Reverse engineer, decompile, or attempt to derive source code</li>
              <li>Use the service to spam, harass, threaten, or exploit others</li>
              <li>Publish content that violates platform policies or laws</li>
              <li>Use the service on behalf of another person or entity without authorization</li>
              <li>Exceed usage limits (if applicable) without permission</li>
            </ul>
          </LegalSection>

          <LegalSection title="8. Disclaimer of Warranties">
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING:</p>
            <ul>
              <li>Uninterrupted or error-free operation</li>
              <li>Accuracy, quality, or completeness of AI-generated content</li>
              <li>Compatibility with all devices, browsers, or platforms</li>
              <li>Achievement of specific marketing results or performance</li>
              <li>That the service will meet your requirements</li>
              <li>That defects will be corrected</li>
            </ul>
            <p style={{marginTop:'12px'}}><em>You use the service entirely at your own risk.</em></p>
          </LegalSection>

          <LegalSection title="9. Limitation of Liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, POSTRIFY SHALL NOT BE LIABLE FOR:</p>
            <ul>
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, information, or business opportunities</li>
              <li>Loss of goodwill or reputational harm</li>
              <li>Any other losses, regardless of their cause or even if advised of the possibility of such damages</li>
              <li>Content published or removed by you or by third parties</li>
              <li>Platform enforcement actions (suspension, removal, policy violations)</li>
              <li>Third-party service failures or interruptions</li>
              <li>Unauthorized access to your account or data</li>
            </ul>
          </LegalSection>

          <LegalSection title="10. Account Termination &amp; Data">
            <p><strong style={{color:'#16152e'}}>Termination by Postrify:</strong> We may suspend or terminate your account immediately if you:</p>
            <ul>
              <li>Violate these Terms of Service</li>
              <li>Engage in fraudulent, abusive, or illegal activity</li>
              <li>Misuse the service or violate platform policies</li>
              <li>Pose a risk to data security or service integrity</li>
            </ul>
            <p style={{marginTop:'12px'}}><strong style={{color:'#16152e'}}>Termination by You:</strong> You may terminate your account at any time via your account settings. Upon termination, your data will be deleted per our <Link to="/data-deletion" style={{color:'#4f46e5', fontWeight:600}}>Data Deletion Policy</Link>.</p>
          </LegalSection>

          <LegalSection title="11. Modifications to Terms">
            <p>We may modify these Terms of Service at any time. Material changes will be communicated via email or prominent notification on the website at least 30 days before becoming effective. Continued use of Postrify after changes constitutes acceptance of the updated Terms.</p>
          </LegalSection>

          <LegalSection title="12. Governing Law &amp; Dispute Resolution">
            <p>These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through good faith negotiation or, if necessary, binding arbitration.</p>
          </LegalSection>

          <LegalSection title="13. Contact Us" last>
            <p style={{ marginBottom:'14px' }}>For questions about these Terms of Service or to report violations:</p>
            <a href="mailto:support.postrify@gmail.com"
              style={{ display:'inline-block', padding:'12px 20px', background:'#e0e7ff', borderRadius:'8px', color:'#3730a3', fontWeight:700, fontSize:'15px', textDecoration:'none' }}>
              support.postrify@gmail.com
            </a>
            <p style={{fontSize:'14px', color:'#666'}}>See also: <Link to="/privacy" style={{color:'#4f46e5', fontWeight:600}}>Privacy Policy</Link> | <Link to="/data-deletion" style={{color:'#4f46e5', fontWeight:600}}>Data Deletion</Link></p>
          </LegalSection>
        </div>

        <div style={{ marginTop:'28px', fontSize:'13px', color:'#8b8aab', display:'flex', gap:'20px', flexWrap:'wrap' }}>
          <Link to="/privacy" style={{ color:'#4f46e5', fontWeight:600 }}>Privacy Policy</Link>
          <Link to="/data-deletion" style={{ color:'#4f46e5', fontWeight:600 }}>Data Deletion</Link>
        </div>
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2026 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

function LegalSection({ title, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : '36px', paddingBottom: last ? 0 : '36px', borderBottom: last ? 'none' : '1px solid #e4e4ef' }}>
      <h2 style={{ fontFamily:'Inter, sans-serif', fontSize:'18px', fontWeight:700, color:'#16152e', marginBottom:'14px' }}>{title}</h2>
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {children}
      </div>
    </div>
  );
}

export default Terms;
